import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { PromotionApi } from 'api';
import type {
  MapCreatePromotionRequest,
  Promotion,
} from 'api/methods/PromotionApi';

/**
 * [Promotion] Query key 생성
 * --
 */
export const promotionQueryKeys = createQueryKeys('promotion', {
  getPromotion: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getPromotions: (params?: { [key: string]: string | number }) => [params],
  getPromotionsMapping: (params?: { [key: string]: string | number }) => [
    params,
  ],
});

/**
 * [Promotion] 프로모션 전체 조회
 * --
 */
export const useGetPromotions = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: promotionQueryKeys.getPromotions(params).queryKey,
    queryFn: () => PromotionApi.getPromotions(params),
    ...options,
  });
  return data;
};

/**
 * [Promotion] 프로모션 전체 조회(맵핑,정책 쪼인)
 * --
 */
export const useGetMapPromotions = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: promotionQueryKeys.getPromotionsMapping(params).queryKey,
    queryFn: () => PromotionApi.getPromotionsMapping(params),
    ...options,
  });

  return data;
};

/**
 * [Promotion] 프로모션 상세 조회
 * --
 */
export const useGetPromotion = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: promotionQueryKeys.getPromotion(idx, params).queryKey,
    queryFn: () => PromotionApi.getPromotion(idx, params),
    ...options,
  });

  return data;
};

/**
 * [Promotion] 프로모션 생성
 * --
 */
export const useCreatePromotion = (body: Promotion) => {
  const { mutateAsync: createPromotion, ...result } = useMutation({
    mutationFn: (variables: Promotion) =>
      PromotionApi.createPromotion(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createPromotion, ...result };
};

/**
 * [Promotion] 프로모션 수정
 * --
 */
export const useUpdatePromotion = (body: Promotion) => {
  const { mutateAsync: updatePromotion, ...result } = useMutation({
    mutationFn: (variables: Promotion) =>
      PromotionApi.updatePromotion(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updatePromotion, ...result };
};

/**
 * [Promotion] 프로모션 삭제
 * --
 */
export const useDeletePromotion = (idx: number) => {
  const { mutateAsync: deletePromotion, ...result } = useMutation({
    mutationFn: (variables: number) => PromotionApi.deletePromotion(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deletePromotion, ...result };
};

/**
 * [Promotion] 프로모션 생성
 * --
 */
export const useUpsertPromotion = () => {
  const { mutateAsync: upsertPromotion, ...result } = useMutation({
    mutationFn: (variables: Promotion[]) =>
      PromotionApi.upsertPromotion(variables),
  });

  return { upsertPromotion, ...result };
};

/**
 * [GroupProduct] 그룹 상품 생성(맵핑, 정책)
 * --
 */
export const useCreateMapPromotion = (body: MapCreatePromotionRequest) => {
  const { mutateAsync: createMapPromotion, ...result } = useMutation({
    mutationFn: (variables: MapCreatePromotionRequest) =>
      PromotionApi.mapCreatePromotion(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createMapPromotion, ...result };
};
